import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Checkbox, Select, Spin } from 'antd'
import PeriodoRangePicker from '../../components/PeriodoRangePicker'
import { usePeriodoCompartilhado } from '../../hooks/usePeriodoCompartilhado'
import { useFiltroCompartilhado } from '../../hooks/useFiltroCompartilhado'
import SideMenu from '../../components/SideMenu'
import MultiFiltro from '../../components/MultiFiltro'
import api from '../../service/api'
import * as D from '../dashboard/styles'
import { RecPanel, RecTitle } from '../recebimentos/styles'
import * as S from './styles'

const { Option } = Select

/* ---------- tipos da API ---------- */

type Registro = {
  status: string
  empresa: string
  dataCredito: string
  administradora: string
  banco: string
  previsto: number
  depositado: number
  diferenca: number
  ajustes: number
  justificativa: string
}

type ConcData = {
  quantidade: number
  valorPrevisto: number
  valorDepositado: number
  diferenca: number
  registros: Registro[]
}

type Filtros = {
  empresas: string[]
  empresasCnpj: Record<string, string>
  administradoras: string[]
  statusPgtoOpcoes: string[]
  bancos: string[]
  anos: number[]
}

/* ---------- helpers ---------- */

const fmtBRL = (n: number) =>
  (n ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const fmtNum = (n: number) => (n ?? 0).toLocaleString('pt-BR')
const fmtDec = (n: number) =>
  (n ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })


const TODOS = 'todos'

/* ---------- componente ---------- */

const Conciliacao: React.FC = () => {
  // escopo por grupo (acesso do cliente via /acessar/:id -> ?grupo=NOME)
  const location = useLocation()
  const grupo = new URLSearchParams(location.search).get('grupo') || ''

  const [filtros, setFiltros] = useState<Filtros>({
    empresas: [],
    empresasCnpj: {},
    administradoras: [],
    statusPgtoOpcoes: [],
    bancos: [],
    anos: [],
  })
  const [empresas, setEmpresas] = useFiltroCompartilhado<string[]>('painel.empresas', [])
  // Período (RangePicker) substitui os antigos filtros Ano/Mês/Dia.
  const [periodo, setPeriodo] = usePeriodoCompartilhado()
  const [administradoras, setAdministradoras] = useState<string[]>([])
  const [statusPgtos, setStatusPgtos] = useState<string[]>([])
  const [bancos, setBancos] = useState<string[]>([])

  const [data, setData] = useState<ConcData | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    api
      .get('/DashboardConciliacao/get-filtros', { params: grupo ? { grupo } : {} })
      .then((res) => {
        const d = res.data?.data ?? {}
        setFiltros({
          empresas: d.empresas ?? [],
          empresasCnpj: d.empresasCnpj ?? {},
          administradoras: d.administradoras ?? [],
          statusPgtoOpcoes: d.statusPgtoOpcoes ?? [],
          bancos: d.bancos ?? [],
          anos: d.anos ?? [],
        })
      })
      .catch(() =>
        setFiltros({ empresas: [], empresasCnpj: {}, administradoras: [], statusPgtoOpcoes: [], bancos: [], anos: [] }),
      )
  }, [grupo])

  const carregar = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, string | number> = {}
      if (grupo) params.NomeDoGrupo = grupo
      if (empresas.length) params.Empresa = empresas.join(',')
      if (periodo?.[0]) params.DataInicio = periodo[0].format('YYYY-MM-DD')
      if (periodo?.[1]) params.DataFim = periodo[1].format('YYYY-MM-DD')
      if (administradoras.length) params.Administradora = administradoras.join(',')
      if (statusPgtos.length) params.StatusPgto = statusPgtos.join(',')
      if (bancos.length) params.Banco = bancos.join(',')

      const res = await api.get('/DashboardConciliacao/get-dashboard-conciliacao', { params })
      setData(res.data?.data ?? null)
    } catch {
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [grupo, empresas, periodo, administradoras, statusPgtos, bancos])

  useEffect(() => {
    carregar()
  }, [carregar])

  const kpis = [
    { t: 'Quantidade', v: fmtNum(data?.quantidade ?? 0) },
    { t: 'Valor Previsto', v: fmtBRL(data?.valorPrevisto ?? 0) },
    { t: 'Valor Depositado', v: fmtBRL(data?.valorDepositado ?? 0) },
    { t: 'Diferença', v: fmtBRL(data?.diferenca ?? 0) },
  ]

  const registros = data?.registros ?? []

  return (
    <D.Page>
      <SideMenu active="conciliacao" />

      <D.Main>
        {loading && (
          <D.LoadingOverlay>
            <Spin size="large" tip="Carregando dados..." />
          </D.LoadingOverlay>
        )}

        <S.FiltersRow>
          <D.Filter>
            <label>Empresa Selecionada</label>
            <Select
              mode="multiple"
              allowClear
              value={empresas}
              onChange={setEmpresas}
              placeholder="Todos"
              maxTagCount="responsive"
              optionLabelProp="label"
              optionFilterProp="label"
              menuItemSelectedIcon={null}
              showArrow
              style={{ width: '100%' }}
            >
              {filtros.empresas.map((e) => {
                const cnpj = filtros.empresasCnpj?.[e] || 'Sem CNPJ'
                return (
                  <Option key={e} value={e} label={e} title={`${e} — CNPJ: ${cnpj}`}>
                    <span title={`CNPJ: ${cnpj}`}>
                      <Checkbox checked={empresas.includes(e)} style={{ marginRight: 8 }} />
                      {e}
                    </span>
                  </Option>
                )
              })}
            </Select>
          </D.Filter>
          <D.Filter style={{ gridColumn: 'span 2' }}>
            <label>Período</label>
            <PeriodoRangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={['Início', 'Fim']}
              value={periodo}
              onChange={(dates) => setPeriodo(dates as [any, any])}
              inputReadOnly
            />
          </D.Filter>
          <D.Filter>
            <label>Administradora</label>
            <MultiFiltro
              value={administradoras}
              onChange={setAdministradoras}
              options={filtros.administradoras.map((a) => ({ value: a, label: a }))}
            />
          </D.Filter>
          <D.Filter>
            <label>Status Pagamento</label>
            <MultiFiltro
              value={statusPgtos}
              onChange={setStatusPgtos}
              options={filtros.statusPgtoOpcoes.map((s) => ({ value: s, label: s }))}
            />
          </D.Filter>
          <D.Filter>
            <label>Banco</label>
            <MultiFiltro
              value={bancos}
              onChange={setBancos}
              options={filtros.bancos.map((b) => ({ value: b, label: b }))}
            />
          </D.Filter>
        </S.FiltersRow>

        <S.Body>
          <S.Content>
            <S.KpiRow4>
              {kpis.map((k) => (
                <S.Kpi key={k.t}>
                  <div className="t">{k.t}</div>
                  <div className="v">{k.v}</div>
                </S.Kpi>
              ))}
            </S.KpiRow4>

            <RecPanel>
              <RecTitle>Registros de Conciliações Bancárias</RecTitle>
              <S.TableScroll>
                <S.ConcTable>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Empresa</th>
                      <th>Data Crédito</th>
                      <th>Administradora</th>
                      <th>Banco</th>
                      <th>Previsto</th>
                      <th>Depositado</th>
                      <th>Diferença</th>
                      <th>Ajustes</th>
                      <th>Justificativa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registros.map((r, i) => (
                      <tr key={i}>
                        <td>{r.status}</td>
                        <td>{r.empresa}</td>
                        <td>{r.dataCredito}</td>
                        <td>{r.administradora}</td>
                        <td>{r.banco}</td>
                        <td>{fmtBRL(r.previsto)}</td>
                        <td>{fmtBRL(r.depositado)}</td>
                        <td>{fmtBRL(r.diferenca)}</td>
                        <td>{fmtDec(r.ajustes)}</td>
                        <td>{r.justificativa}</td>
                      </tr>
                    ))}
                  </tbody>
                </S.ConcTable>
              </S.TableScroll>
            </RecPanel>
          </S.Content>
        </S.Body>
      </D.Main>
    </D.Page>
  )
}

export default Conciliacao
